import { CalendarIcon } from '@radix-ui/react-icons';
import { useId } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

export interface DateRange {
  /** `YYYY-MM-DD`, inclusive. */
  from?: string;
  /** `YYYY-MM-DD`, inclusive. */
  to?: string;
}

const day = (offset: number) => {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() + offset);
  return d.toISOString().slice(0, 10);
};

const PRESETS: { label: string; range: () => DateRange }[] = [
  { label: 'Today', range: () => ({ from: day(0), to: day(0) }) },
  { label: 'Last 7 days', range: () => ({ from: day(-6), to: day(0) }) },
  { label: 'Last 30 days', range: () => ({ from: day(-29), to: day(0) }) },
];

/** `2026-09-01 – 2026-09-26`, `From 2026-09-01`, `Until …`. */
export function describeRange({ from, to }: DateRange): string | null {
  if (from && to) return from === to ? from : `${from} – ${to}`;
  if (from) return `From ${from}`;
  if (to) return `Until ${to}`;
  return null;
}

/**
 * A UTC date range for a list filter, stored as `from`/`to` search params (`YYYY-MM-DD`).
 * Map it to server filters as `gte from` and `lt (to + 1 day)` so the last day is included.
 */
export function DataTableDateFilter({
  label,
  value,
  onChange,
  className,
}: {
  label: string;
  value: DateRange;
  onChange: (value: DateRange) => void;
  className?: string;
}) {
  const id = useId();
  const summary = describeRange(value);
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className={cn('font-normal', summary && 'border-primary/50', className)}>
          <CalendarIcon />
          <span className="text-muted-foreground">{label}:</span>
          {summary ?? 'Any time'}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-72 space-y-3">
        <div className="flex flex-wrap gap-1">
          {PRESETS.map((preset) => (
            <Button key={preset.label} variant="secondary" size="xs" onClick={() => onChange(preset.range())}>
              {preset.label}
            </Button>
          ))}
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div className="grid gap-1">
            <Label htmlFor={`${id}-from`} className="text-xs">
              From (UTC)
            </Label>
            <Input
              id={`${id}-from`}
              type="date"
              value={value.from ?? ''}
              max={value.to}
              onChange={(e) => onChange({ ...value, from: e.target.value || undefined })}
            />
          </div>
          <div className="grid gap-1">
            <Label htmlFor={`${id}-to`} className="text-xs">
              To (UTC)
            </Label>
            <Input
              id={`${id}-to`}
              type="date"
              value={value.to ?? ''}
              min={value.from}
              onChange={(e) => onChange({ ...value, to: e.target.value || undefined })}
            />
          </div>
        </div>
        {summary && (
          <Button variant="ghost" size="sm" className="w-full" onClick={() => onChange({})}>
            Any time
          </Button>
        )}
      </PopoverContent>
    </Popover>
  );
}

/** `YYYY-MM-DD` plus one day, for an exclusive upper bound. */
export function nextDay(date: string): string {
  const d = new Date(`${date}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + 1);
  return d.toISOString().slice(0, 10);
}
